import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Smartphone, Briefcase, Zap, Search, BarChart3, Phone, MessageSquare, CheckCircle, XCircle, AlertTriangle, Target, TrendingUp, RefreshCw, Lightbulb, Mail, Globe, Bot, Megaphone, BookOpen, Camera, Rocket, Gem } from 'lucide-react';

function AuditGratuit() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    site_web: '',
    message: '',
    source: 'audit-gratuit'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    axios.post(`${API_URL}/api/link-clicks/audit-gratuit`, {
      language: navigator.language,
      platform: navigator.userAgentData?.platform || navigator.platform,
      screen_width: window.screen.width,
      screen_height: window.screen.height
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/leads/public`, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Une erreur est survenue. Veuillez reessayer.');
    } finally {
      setLoading(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const tools = [
    { icon: Globe, label: 'Sites Web' },
    { icon: ShoppingCart, label: 'E-commerce' },
    { icon: Smartphone, label: 'Applications' },
    { icon: Briefcase, label: 'CRM' },
    { icon: Bot, label: 'IA & Chatbots' },
    { icon: Zap, label: 'Automatisation' },
    { icon: Search, label: 'SEO' },
    { icon: Megaphone, label: 'Google Ads' },
    { icon: BookOpen, label: 'Facebook Ads' },
    { icon: Camera, label: 'Instagram Ads' }
  ];

  const features = [
    { icon: Search, title: 'Analyse approfondie', desc: 'Audit complet de votre presence digitale' },
    { icon: BarChart3, title: 'Rapport detaille', desc: 'Recommandations personnalisees' },
    { icon: Rocket, title: 'Accompagnement expert', desc: 'Un consultant dedie pour vous' },
    { icon: Gem, title: '100% Gratuit', desc: 'Sans engagement, sans frais' }
  ];

  if (success) {
    return (
      <div className="audit-page">
        <style>{getStyles()}</style>
        <div className="bg-gradient"></div>
        <div className="bg-grid"></div>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h1>Demande enregistree !</h1>
            <p>Merci pour votre confiance. Notre equipe d'experts va analyser votre demande et vous recontacter tres rapidement.</p>
            <div className="success-info">
              <strong>Prochaine etape</strong>
              <span>Un consultant vous contactera sous 24h pour planifier votre audit gratuit.</span>
            </div>
            <a href="/" className="back-btn">← Retour a l'accueil</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-page">
      <style>{getStyles()}</style>

      <div className="bg-gradient"></div>
      <div className="bg-grid"></div>

      {/* Floating Elements */}
      <div className="floating-elements">
        <div className="floating-circle c1"></div>
        <div className="floating-circle c2"></div>
        <div className="floating-circle c3"></div>
      </div>

      {/* Header */}
      <header className="header">
        <div className="logo">
          <div className="logo-icon">E</div>
          <span className="logo-text">E-Shops PRO</span>
        </div>
        <div className="date-text">{currentDate}</div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {/* Left Column */}
        <div className="left-column">
          <div className="badge">
            <span className="badge-dot"></span>
            <span>Offre limitee</span>
          </div>

          <h1 className="main-title">
            <span className="title-white">Audit Digital</span>
            <br />
            <span className="title-gradient">100% Gratuit</span>
          </h1>

          <p className="subtitle">
            Decouvrez le potentiel cache de votre entreprise avec notre audit complet realise par des experts du digital.
          </p>

          {/* Tools Pills */}
          <div className="tools-section">
            {tools.map((tool, index) => (
              <div key={index} className="tool-pill">
                <tool.icon size={16} />
                <span>{tool.label}</span>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon"><feature.icon size={24} /></div>
                <div className="feature-title">{feature.title}</div>
                <div className="feature-desc">{feature.desc}</div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-number">6+</div>
              <div className="stat-label">Ans d'experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Audits realises</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Clients satisfaits</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24h</div>
              <div className="stat-label">Delai de reponse</div>
            </div>
          </div>
        </div>

        {/* Right Column - Form */}
        <div className="right-column">
          <div className="form-card">
            <div className="form-header">
              <h2>Demander mon audit</h2>
              <p>Remplissez le formulaire et recevez votre audit personnalise</p>
            </div>

            {error && <div className="error-msg">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="input-group">
                <label>Nom complet <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  onFocus={() => setFocusedField('nom')}
                  onBlur={() => setFocusedField(null)}
                  required
                  placeholder="Jean Dupont"
                  className={focusedField === 'nom' ? 'focused' : ''}
                />
              </div>

              <div className="input-group">
                <label>Email professionnel <span className="required">*</span></label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  placeholder="jean@entreprise.fr"
                  className={focusedField === 'email' ? 'focused' : ''}
                />
              </div>

              <div className="input-row">
                <div className="input-group">
                  <label>Telephone <span className="required">*</span></label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    onFocus={() => setFocusedField('telephone')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="06 12 34 56 78"
                    className={focusedField === 'telephone' ? 'focused' : ''}
                  />
                </div>

                <div className="input-group">
                  <label>Entreprise <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                    onFocus={() => setFocusedField('entreprise')}
                    onBlur={() => setFocusedField(null)}
                    required
                    placeholder="Votre societe"
                    className={focusedField === 'entreprise' ? 'focused' : ''}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Site web actuel</label>
                <input
                  type="text"
                  value={formData.site_web}
                  onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                  onFocus={() => setFocusedField('site_web')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="www.votresite.fr"
                  className={focusedField === 'site_web' ? 'focused' : ''}
                />
              </div>

              <div className="input-group">
                <label>Votre projet ou vos besoins</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Decrivez vos objectifs..."
                  rows={3}
                  className={focusedField === 'message' ? 'focused' : ''}
                />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Obtenir mon audit gratuit →'}
              </button>

              <p className="disclaimer">
                En soumettant ce formulaire, vous acceptez d'etre contacte par notre equipe.
                Vos donnees sont protegees.
              </p>

              <div className="contact-buttons">
                <a href="https://wa.me/33743678501?text=Bonjour%2C%20je%20souhaite%20obtenir%20un%20audit%20gratuit%20pour%20mon%20entreprise." target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                  <MessageSquare size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> WhatsApp
                </a>
                <a href="tel:+33743678501" className="phone-btn">
                  <Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> 07 43 67 85 01
                </a>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

function getStyles() {
  return `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    .audit-page {
      min-height: 100vh;
      background: #0a0a0a;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      position: relative;
      overflow-x: hidden;
    }

    .bg-gradient {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background:
        radial-gradient(ellipse at 20% 20%, rgba(120, 119, 198, 0.15) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 80%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse at 50% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
      pointer-events: none;
      z-index: 0;
    }

    .bg-grid {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background-image:
        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
      background-size: 50px 50px;
      pointer-events: none;
      z-index: 0;
    }

    .floating-elements {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      pointer-events: none;
      z-index: 1;
      overflow: hidden;
    }

    .floating-circle {
      position: absolute;
      border-radius: 50%;
      animation: float 20s ease-in-out infinite;
    }

    .c1 {
      width: 300px; height: 300px;
      background: radial-gradient(circle, rgba(102,126,234,0.1) 0%, transparent 70%);
      top: 10%; left: -5%;
      animation-delay: 0s;
    }

    .c2 {
      width: 400px; height: 400px;
      background: radial-gradient(circle, rgba(168,85,247,0.08) 0%, transparent 70%);
      top: 50%; right: -10%;
      animation-delay: -7s;
    }

    .c3 {
      width: 250px; height: 250px;
      background: radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%);
      bottom: 10%; left: 30%;
      animation-delay: -14s;
    }

    @keyframes float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(30px, -30px) scale(1.05); }
      50% { transform: translate(-20px, 20px) scale(0.95); }
      75% { transform: translate(20px, 30px) scale(1.02); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.5; transform: scale(1.2); }
    }

    /* Header */
    .header {
      position: relative;
      z-index: 10;
      padding: 25px 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .logo-icon {
      width: 42px; height: 42px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 800;
      color: white;
      box-shadow: 0 4px 15px rgba(102,126,234,0.4);
    }

    .logo-text {
      font-size: 20px;
      font-weight: 700;
      background: linear-gradient(135deg, #fff 0%, #a0a0a0 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .date-text {
      color: rgba(255,255,255,0.4);
      font-size: 13px;
      text-transform: capitalize;
    }

    /* Main Content */
    .main-content {
      position: relative;
      z-index: 10;
      display: flex;
      max-width: 1400px;
      margin: 0 auto;
      padding: 50px 40px 80px;
      gap: 60px;
      align-items: flex-start;
    }

    .left-column {
      flex: 1 1 55%;
      min-width: 0;
    }

    .right-column {
      flex: 1 1 400px;
      max-width: 420px;
      position: sticky;
      top: 30px;
    }

    /* Badge */
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(102,126,234,0.1);
      border: 1px solid rgba(102,126,234,0.3);
      border-radius: 50px;
      margin-bottom: 25px;
      color: #667eea;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .badge-dot {
      width: 8px; height: 8px;
      background: #667eea;
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    /* Title */
    .main-title {
      font-size: 52px;
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 20px;
      letter-spacing: -2px;
    }

    .title-white { color: #fff; }

    .title-gradient {
      background: linear-gradient(135deg, #667eea 0%, #a855f7 50%, #ec4899 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .subtitle {
      font-size: 17px;
      line-height: 1.7;
      color: rgba(255,255,255,0.6);
      margin-bottom: 30px;
      max-width: 500px;
    }

    /* Tools Pills */
    .tools-section {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 40px;
    }

    .tool-pill {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 50px;
      font-size: 13px;
      color: rgba(255,255,255,0.8);
      transition: all 0.3s ease;
    }

    .tool-pill:hover {
      background: rgba(102,126,234,0.1);
      border-color: rgba(102,126,234,0.3);
      transform: translateY(-2px);
    }

    /* Features Grid */
    .features-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;
      margin-bottom: 35px;
    }

    .feature-card {
      padding: 20px;
      background: rgba(255,255,255,0.02);
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,0.05);
      transition: all 0.3s ease;
    }

    .feature-card:hover {
      background: rgba(255,255,255,0.04);
      border-color: rgba(102,126,234,0.2);
      transform: translateY(-3px);
    }

    .feature-icon {
      font-size: 28px;
      margin-bottom: 12px;
    }

    .feature-title {
      font-size: 15px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 6px;
    }

    .feature-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.5);
      line-height: 1.4;
    }

    /* Stats */
    .stats-row {
      display: flex;
      gap: 30px;
      padding-top: 25px;
      border-top: 1px solid rgba(255,255,255,0.08);
    }

    .stat-item {
      text-align: left;
    }

    .stat-number {
      font-size: 32px;
      font-weight: 800;
      background: linear-gradient(135deg, #fff 0%, #667eea 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .stat-label {
      font-size: 12px;
      color: rgba(255,255,255,0.4);
      margin-top: 4px;
    }

    /* Form Card */
    .form-card {
      background: rgba(255,255,255,0.03);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      border: 1px solid rgba(255,255,255,0.08);
      padding: 35px;
      box-shadow: 0 25px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05);
    }

    .form-header {
      margin-bottom: 25px;
      text-align: center;
    }

    .form-header h2 {
      font-size: 24px;
      font-weight: 700;
      color: #fff;
      margin-bottom: 8px;
    }

    .form-header p {
      font-size: 14px;
      color: rgba(255,255,255,0.5);
    }

    .input-group {
      margin-bottom: 18px;
    }

    .input-row {
      display: flex;
      gap: 12px;
    }

    .input-row .input-group {
      flex: 1;
    }

    .input-group label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: rgba(255,255,255,0.7);
      margin-bottom: 8px;
    }

    .required {
      color: #ec4899;
    }

    .input-group input,
    .input-group textarea {
      width: 100%;
      padding: 14px 16px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 10px;
      font-size: 14px;
      color: #fff;
      outline: none;
      transition: all 0.3s ease;
      box-sizing: border-box;
      font-family: inherit;
    }

    .input-group input::placeholder,
    .input-group textarea::placeholder {
      color: rgba(255,255,255,0.3);
    }

    .input-group input.focused,
    .input-group textarea.focused {
      border-color: #667eea;
      background: rgba(102,126,234,0.05);
      box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
    }

    .input-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .submit-btn {
      width: 100%;
      padding: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 700;
      color: #fff;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(102,126,234,0.4);
      margin-top: 5px;
    }

    .submit-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102,126,234,0.5);
    }

    .submit-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .disclaimer {
      text-align: center;
      font-size: 11px;
      color: rgba(255,255,255,0.35);
      margin-top: 15px;
      line-height: 1.5;
    }

    .contact-buttons {
      display: flex;
      gap: 10px;
      margin-top: 20px;
      justify-content: center;
    }

    .whatsapp-btn, .phone-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 18px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .whatsapp-btn {
      background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(37,211,102,0.3);
    }

    .whatsapp-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37,211,102,0.4);
    }

    .phone-btn {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.2);
      color: white;
    }

    .phone-btn:hover {
      background: rgba(255,255,255,0.1);
      transform: translateY(-2px);
    }

    .error-msg {
      background: rgba(239,68,68,0.1);
      border: 1px solid rgba(239,68,68,0.3);
      color: #ef4444;
      padding: 12px 15px;
      border-radius: 10px;
      margin-bottom: 18px;
      font-size: 13px;
    }

    /* Success */
    .success-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
    }

    .success-card {
      background: rgba(255,255,255,0.03);
      backdrop-filter: blur(20px);
      border-radius: 24px;
      border: 1px solid rgba(255,255,255,0.08);
      padding: 50px;
      text-align: center;
      max-width: 450px;
    }

    .success-icon {
      width: 80px; height: 80px;
      background: linear-gradient(135deg, #10b981, #059669);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 25px;
      font-size: 40px;
      color: white;
      box-shadow: 0 10px 30px rgba(16,185,129,0.3);
    }

    .success-card h1 {
      font-size: 28px;
      color: #fff;
      margin-bottom: 15px;
    }

    .success-card p {
      color: rgba(255,255,255,0.6);
      line-height: 1.7;
      margin-bottom: 25px;
    }

    .success-info {
      background: rgba(16,185,129,0.1);
      border: 1px solid rgba(16,185,129,0.2);
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 25px;
    }

    .success-info strong {
      display: block;
      color: #10b981;
      margin-bottom: 8px;
    }

    .success-info span {
      color: rgba(255,255,255,0.6);
      font-size: 14px;
    }

    .back-btn {
      display: inline-block;
      padding: 14px 28px;
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 10px;
      color: #fff;
      text-decoration: none;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .back-btn:hover {
      background: rgba(255,255,255,0.1);
    }

    /* Responsive */
    @media (max-width: 1024px) {
      .main-content {
        flex-direction: column;
        padding: 30px 20px 60px;
        gap: 40px;
      }

      .left-column, .right-column {
        max-width: 100%;
        flex: 1 1 auto;
      }

      .right-column {
        position: relative;
        top: 0;
      }

      .main-title {
        font-size: 40px;
      }

      .features-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 768px) {
      .header {
        padding: 20px;
      }

      .date-text {
        display: none;
      }

      .main-title {
        font-size: 32px;
        letter-spacing: -1px;
      }

      .subtitle {
        font-size: 15px;
      }

      .features-grid {
        grid-template-columns: 1fr;
        gap: 12px;
      }

      .feature-card {
        padding: 16px;
        display: flex;
        align-items: center;
        gap: 15px;
      }

      .feature-icon {
        margin-bottom: 0;
        font-size: 24px;
      }

      .feature-card > div:last-child {
        flex: 1;
      }

      .stats-row {
        justify-content: space-between;
      }

      .stat-number {
        font-size: 26px;
      }

      .form-card {
        padding: 25px 20px;
      }

      .input-row {
        flex-direction: column;
        gap: 0;
      }

      .contact-buttons {
        flex-direction: column;
      }

      .whatsapp-btn, .phone-btn {
        justify-content: center;
      }

      .tools-section {
        gap: 6px;
      }

      .tool-pill {
        padding: 6px 12px;
        font-size: 12px;
      }
    }

    @media (max-width: 480px) {
      .main-title {
        font-size: 28px;
      }

      .badge {
        font-size: 11px;
        padding: 6px 12px;
      }

      .stats-row {
        gap: 15px;
      }

      .stat-number {
        font-size: 22px;
      }

      .stat-label {
        font-size: 11px;
      }
    }
  `;
}

export default AuditGratuit;
